import { useState } from 'react';
import { IoAddCircleOutline, IoTrashOutline } from 'react-icons/io5';
import toast from 'react-hot-toast';

import Spinner from './Spinner';

// --- Constants ---
const TRANSPORT_OPTIONS = [
  { id: 'sse', label: 'SSE' },
  { id: 'streamable_http', label: 'Streamable HTTP' },
];

const AddConnection = ({
  onCheck,
  onInitiateOauth,
  onSaveCustom,
  onSaveStdio,
  checkResult,
  isChecking,
  onClose,
}) => {
  const [name, setName] = useState('');
  const [transportType, setTransportType] = useState('sse'); // 'sse' or 'streamablehttp' or 'stdio'

  // SSE state
  const [url, setUrl] = useState('');
  const [headers, setHeaders] = useState([{ id: 1, key: '', value: '' }]);

  // --- Dynamic Content based on Transport Type ---
  const urlLabel =
    transportType === 'sse' ? 'MCP SSE URL' : 'MCP HTTP Stream URL';
  const urlPlaceholder =
    transportType === 'sse'
      ? 'https://mcp.provider.com/sse'
      : 'https://mcp.provider.com/stream';

  // --- NEW: Handlers for managing the dynamic headers list ---

  // Adds a new, empty header row
  const handleAddHeader = () => {
    setHeaders([...headers, { id: Date.now(), key: '', value: '' }]);
  };

  // Removes a header row by its unique ID
  const handleRemoveHeader = (id) => {
    // Prevent removing the very last row to avoid an empty state
    if (headers.length > 1) {
      setHeaders(headers.filter((header) => header.id !== id));
    } else {
      // Clear the last row instead of removing it
      setHeaders([{ id: 1, key: '', value: '' }]);
    }
  };

  // Updates a specific header's key or value
  const handleHeaderChange = (id, field, value) => {
    setHeaders(
      headers.map((header) =>
        header.id === id ? { ...header, [field]: value } : header,
      ),
    );
  };

  // --- Action Handlers ---
  const handleCheck = () => {
    if (!url) {
      toast.error('URL cannot be empty.');
      return;
    }
    // Validate URL format based on the selected transport type
    if (transportType === 'sse' && !url.endsWith('/sse')) {
      toast.error('SSE URL must end with /sse');
      return;
    }
    onCheck(url, transportType);
  };

  const handleOauth = () => {
    if (name && checkResult?.origin) {
      onInitiateOauth({
        connection_name: name,
        sse_url: url,
        origin: checkResult.origin,
        transport_type: transportType,
      });
    }
  };

  const handleCustomSave = () => {
    try {
      // const parsedHeaders = JSON.parse(headers);
      if (!name || !url) {
        toast.error('Connection Name and URL are required.');
        return;
      }

      const finalHeaders = {};

      // 1. Validate all header rows before attempting to build the final object.
      for (const header of headers) {
        const key = header.key.trim();
        const value = header.value.trim();

        // Ignore rows where both key and value are empty
        if (!key && !value) {
          continue;
        }

        // If a key or value is missing, it's an error.
        if (!key || !value) {
          toast.error(
            'All headers must have both a name and a value. Please fill in or remove the incomplete row.',
          );
          return; // Stop the save process
        }

        // If valid, add to our final object.
        finalHeaders[key] = value;
      }

      if (name && url) {
        onSaveCustom({
          connection_name: name,
          sse_url: url,
          headers: finalHeaders,
          transport_type: transportType,
        });

        if (onClose) onClose();
      }
    } catch {
      toast.error('Custom headers are not valid JSON.');
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 p-6 text-zinc-300">
      {/* --- Transport Type Tabs --- */}
      <div>
        <label
          htmlFor="transport-type"
          className="mb-2 block text-sm font-medium text-zinc-400"
        >
          Transport Type
        </label>

        <div className="flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-800 p-1">
          {TRANSPORT_OPTIONS.map((option) => (
            <button
              key={option.id}
              onClick={() => setTransportType(option.id)}
              className={`w-full rounded-md px-4 py-2 text-sm font-medium transition-all duration-200
                            ${
                              transportType === option.id
                                ? 'bg-zinc-700 text-white'
                                : 'bg-transparent text-zinc-400 hover:bg-zinc-700/50'
                            }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* --- Core Connection Details --- */}
      <div className="space-y-4">
        <div>
          <label
            htmlFor="connectionName"
            className="text-sm font-medium text-zinc-400"
          >
            Connection Name
          </label>
          <input
            id="connectionName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            type="text"
            placeholder="e.g., My Project Connection"
            className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-600"
          />
        </div>

        <div>
          <label htmlFor="mcpUrl" className="text-sm font-medium text-zinc-400">
            {urlLabel}
          </label>
          <input
            id="mcpUrl"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            type="text"
            placeholder={urlPlaceholder}
            className="mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-600"
          />
        </div>
      </div>

      <button
        onClick={handleCheck}
        disabled={!url || isChecking}
        className="flex items-center gap-2 rounded-lg bg-zinc-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-zinc-600 disabled:opacity-50"
      >
        {isChecking && <Spinner size="1.2rem" />}
        Check URL
      </button>

      {/* --- Post-Check Results & Actions --- */}
      {checkResult && (
        <div className="mt-6 space-y-6 border-t border-zinc-700 pt-6">
          {/* OAuth Section */}
          {checkResult.oauth_available && (
            <div className="rounded-xl border border-zinc-700 bg-zinc-800 p-4">
              <p className="text-sm text-zinc-300">
                This provider supports OAuth 2.0 for setup.
              </p>

              <button
                onClick={handleOauth}
                disabled={!name}
                className="mt-4 rounded-lg bg-zinc-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-zinc-600 disabled:opacity-50"
              >
                Connect with OAuth
              </button>
            </div>
          )}

          {/* Custom Headers Section */}
          <details className="group" open={!checkResult.oauth_available}>
            <summary className="cursor-pointer list-none text-sm font-medium text-white">
              {checkResult.oauth_available
                ? 'Or Connect with Custom Headers'
                : 'Configure Custom Headers'}
            </summary>

            <div className="mt-4 space-y-3 border-l border-zinc-700 pl-4">
              {headers.map((header) => (
                <div key={header.id} className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="Header Name"
                    value={header.key}
                    onChange={(e) =>
                      handleHeaderChange(header.id, 'key', e.target.value)
                    }
                    className="w-1/3 rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-600"
                  />

                  <input
                    type="text"
                    placeholder="Header Value"
                    value={header.value}
                    onChange={(e) =>
                      handleHeaderChange(header.id, 'value', e.target.value)
                    }
                    className="flex-1 rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-600"
                  />

                  <button
                    onClick={() => handleRemoveHeader(header.id)}
                    className="p-2 text-zinc-400 transition hover:text-zinc-200"
                  >
                    <IoTrashOutline className="h-5 w-5" />
                  </button>
                </div>
              ))}

              <button
                onClick={handleAddHeader}
                className="flex items-center gap-2 pt-2 text-sm text-zinc-300 transition hover:text-white"
              >
                <IoAddCircleOutline className="h-5 w-5" />
                Add Another Header
              </button>
            </div>

            <button
              onClick={handleCustomSave}
              disabled={!name}
              className="mt-5 rounded-lg bg-zinc-700 px-5 py-2 text-sm font-medium text-white transition hover:bg-zinc-600 disabled:opacity-50"
            >
              Save Custom Connection
            </button>
          </details>
        </div>
      )}
    </div>
  );
};

export default AddConnection;
